import { useAtom } from "jotai";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { welcomePath } from "@/components/onboarding/welcome";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Shortcut } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { projectDialogAtom } from "@/store/ui";
import { useModelActions, useRecord } from "@/sync/hooks";
import type { Project } from "@/sync/models/project";

/** Create/edit project. Both paths are optimistic: the dialog closes before the server answers. */
export function ProjectDialog() {
	const [state, setState] = useAtom(projectDialogAtom);
	const editing = useRecord("project", state?.mode === "edit" ? state.projectId : undefined);
	const close = () => setState(null);

	return (
		<Dialog open={!!state} onOpenChange={(open) => !open && close()}>
			<DialogContent>
				<ProjectForm key={editing?.id ?? "new"} project={editing} onDone={close} />
			</DialogContent>
		</Dialog>
	);
}

function ProjectForm({ project, onDone }: { project?: Project; onDone: () => void }) {
	const projects = useModelActions("project");
	const navigate = useNavigate();
	const [name, setName] = useState(project?.name ?? "");
	const [description, setDescription] = useState(project?.description ?? "");
	const trimmed = name.trim();

	const submit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!trimmed) return;
		if (project) {
			projects.update(project.id, { name: trimmed, description });
		} else {
			const created = projects.create({
				name: trimmed,
				description,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC",
			});
			navigate(welcomePath(created.id));
		}
		onDone();
	};

	return (
		<form
			onSubmit={submit}
			onKeyDown={(e) => {
				if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) e.currentTarget.requestSubmit();
			}}
			className="grid gap-4"
		>
			<DialogHeader>
				<span className="mono-label">{project ? "Edit project" : "New project"}</span>
				<DialogTitle>{project ? "Project details" : "Create a project"}</DialogTitle>
				<DialogDescription>
					A project is one brand: its channels, content, strategy, plan and agents.
				</DialogDescription>
			</DialogHeader>
			<div className="grid gap-2">
				<Label htmlFor="project-name">Name</Label>
				<Input
					id="project-name"
					autoFocus
					value={name}
					maxLength={80}
					placeholder="Acme Coffee"
					onChange={(e) => setName(e.target.value)}
				/>
			</div>
			<div className="grid gap-2">
				<Label htmlFor="project-description">Description</Label>
				<Textarea
					id="project-description"
					value={description}
					maxLength={2000}
					placeholder="Specialty roaster in Lisbon. Playful, warm, a little nerdy."
					onChange={(e) => setDescription(e.target.value)}
				/>
			</div>
			<DialogFooter>
				<Button type="button" variant="ghost" onClick={onDone}>
					Cancel
				</Button>
				<Button type="submit" disabled={!trimmed}>
					{project ? "Save" : "Create project"}
					<Shortcut
						keys="enter"
						className="ml-1 [&_kbd]:border-primary-foreground/20 [&_kbd]:bg-primary-foreground/10 [&_kbd]:text-primary-foreground/80"
					/>
				</Button>
			</DialogFooter>
		</form>
	);
}
